import {LoaderFunctionArgs} from 'react-router-dom';
import {getRootStore} from '../../app.bootstrap';
import {getAppTracer} from '../../../app.tracer';
import {RouteName} from '../../router/route-names';
import {LOG_EVENTS} from '@techsee/techsee-common/lib/constants/event-logs.constants';
import {GuestStore, IGuestStore, GuestUrlParams, GuestObserverDetails} from './guest.stores';

const trace = getAppTracer('guest.loader');

function extractGuestParams(url: URL): GuestUrlParams {
    const p = url.searchParams;
    return {
        ott: p.get('ott') ?? undefined,
        g: p.get('g') ?? undefined,
        roomId: p.get('roomId') ?? undefined,
        roomCode: p.get('roomCode') ?? undefined,
        audio: p.get('audio') ?? undefined,
        shortId: p.get('shortId') ?? undefined
    };
}

export async function guestRouteLoader(args: LoaderFunctionArgs): Promise<IGuestStore | void> {
    trace.info('Starting');
    const rootStore = getRootStore();
    const url = new URL(args.request.url);
    const params = extractGuestParams(url);
    const ott = params.ott || '';
    const room = params.g || params.roomId;
    const nav = rootStore.tsReactStateHelper;

    let token: string;
    try {
        token = await rootStore.generalApiService.ottRedeem({ott});
    } catch (e) {
        if (e instanceof Response) throw e;
        rootStore.eventsLogService.error({
            logType: LOG_EVENTS.guestObserverRedeemOttFailed.type,
            meta: {
                roomIdOrGuid: room,
                error: `Guest failed to redeemOtt using ott: ${ott}, with error: ${e}`
            }
        });
        await nav.navigateTo(RouteName.INVITATION_ENDED, {err: true});

        return;
    }

    rootStore.eventsLogService.info({
        logType: LOG_EVENTS.guestObserverRedeemOttSuccessful,
        meta: {ott, roomIdOrGuid: room}
    });

    await rootStore.localizationService.init();

    let observerDetails: GuestObserverDetails = {};
    try {
        const res = (await rootStore.observationsService.guestObserverDetails({hint: ott}, token)) as {
            deepLink?: string;
        };
        if (res?.deepLink) {
            observerDetails = {
                deepLink: res.deepLink,
                isSf: true,
                isMini: true,
                isEmb: true
            };
        }
    } catch (err) {
        if (err instanceof Response) throw err;
        rootStore.eventsLogService.error({
            logType: LOG_EVENTS.guestObserverDetailsFailed.type,
            meta: {
                roomIdOrGuid: room,
                error: `failed GET guestObserverDetails using hint: ${ott}, with error: ${err}`
            }
        });
        await nav.navigateTo(RouteName.INVITATION_ENDED, {err: true});

        return;
    }

    const store = new GuestStore(token, params, observerDetails);
    trace.info('Completed successfully');
    return store;
}
